import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Anchor,
  Badge,
  Button,
  Group,
  Menu,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconArchive, IconEye, IconFile } from "@tabler/icons-react";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
  TruncatedCell,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { DEFAULT_PAGINATION, ROLES } from "@constants/index";

import { useArchiveConsultation } from "@hooks/mutations/consultation/useArchiveConsultation";
import { useGenerateConsultationReport } from "@hooks/mutations/consultation/useGenerateConsultationReport";
import { useToggleConsultationRelease } from "@hooks/mutations/consultation/useToggleConsultationRelease";
import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { useAuth } from "@contexts/auth";


interface ConsultationTableProps {
  setIsRefreshing: (arg: boolean) => void;
  searchFilter: string;
  currentPage: number;
  setCurrentPage: (arg: number) => void;
  includeArchived: boolean;
}

export const ConsultationsTable = ({
  setIsRefreshing,
  searchFilter,
  currentPage,
  setCurrentPage,
  includeArchived,
}: ConsultationTableProps) => {
  const { t, i18n } = useTranslation();

  const { roles , can } = useAuth();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [debounced] = useDebouncedValue(searchFilter, 400);

  const [currentConsultationId, setCurrentConsultationId] = useState(""); //! Doesn't always update the current button

  const { columnCount, tableRef } = useTableColumnCount();

  const {
    data,
    isError,
    isSuccess,
    isLoading,
    fetchStatus,
    refetch,
    isRefetching,
  } = useGetConsultationsForPatient({
    patientId: patientId as string,
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    search: debounced,
    includeArchived,
  });
  const { mutate: generateConsultationReport } =
    useGenerateConsultationReport();
  const { mutate: archiveConsultation } = useArchiveConsultation();
  const {
    mutate: toggleConsultationRelease,
    isLoading: isToggleConsultationReleaseLoading,
  } = useToggleConsultationRelease();

  const handleErrors = useQueryErrorHandling();

  const consultationRows = useMemo(() => {
    return data?.newData?.map(item => (
      <Table.Tr key={item.id}>
        {/* <Table.Td>
          {item.consultationMethod === "fast" ? (
            <div className="icon-wrapper">
              <MdOutlineBolt className="bolt-icon" />
            </div>
          ) : null}
        </Table.Td> */}
        <Table.Td>
          <Group gap={8} align="start">
            <Stack gap={5}>
              <Text fz={14} c="black" ff={"var(--font-secondary)"} fw={450}>
                {item.consultationMethod === "standard" ? (
                  <Anchor
                    fz={14}
                    component={Link}
                    c={"accent.4"}
                    to={`general/${item.id}`}
                    state={{ selectedConsultation: item }}
                    className={"hover-underline"}
                    td="none"
                    ff={"var(--font-secondary)"}
                    fw={450}>
                    {item.cId}
                  </Anchor>
                ) : (
                  item.cId
                )}
              </Text>
              <Text c="dimmed" fz={13}>
                {formatDateToString(item.date as Date, true)}
              </Text>
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td>
          <TruncatedCell value={item.physician?.fullName} />
        </Table.Td>
        <Table.Td>
          <Badge
            fz={12}
            bg={
              item.status?.toLocaleLowerCase() === "incomplete"
                ? "yellow.0"
                : "green.0"
            }
            c={
              item.status?.toLocaleLowerCase() === "incomplete"
                ? "yellow.9"
                : "green.9"
            }>
            {t(`${item.status}`)}
          </Badge>
          {item.archived && (
            <Badge fz={12} color="gray" ml="xs">
              {t("archived")}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>
          <PermissionGate permission="consultations.release"><Button
            w={"auto"}
            tt={"capitalize"}
            variant="light"
            size="compact-sm"
            disabled={item.status === "incomplete"}
            color={item.visibility === "released" ? "green" : "red"}
            loading={
              isToggleConsultationReleaseLoading &&
              currentConsultationId === item.id
            }
            onClick={e => {
              setCurrentConsultationId(item.id as string);
              const id = showLoadingNotification({});
              toggleConsultationRelease(
                {
                  consultationId: item.id as string,
                  patientId: item.patientId as string,
                  visibility:
                    item.visibility === "released" ? "unrelease" : "release",
                },
                {
                  onSuccess: () => {
                    showSuccessNotification({
                      id,
                      title: "success",
                      message: "update-consultation-success",
                    });
                    refetch();
                  },
                  onError: (res: any) => {
                    handleErrors(res, "mutation", id);
                  },
                }
              );
              e.stopPropagation();
            }}>
            {t(`${item?.visibility}`)}
          </Button></PermissionGate>
        </Table.Td>
        <Table.Td>
          <Menu withArrow position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="outline"
                size="compact-sm"
                onClick={() => setCurrentConsultationId(item.id)}>
                {t("action")}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                disabled={item.consultationMethod === "fast"}
                leftSection={<IconEye size={16} stroke={1.5} />}
                onClick={() => {
                  navigate(`general/${item.id}`);
                }}>
                {t("view-consultation")}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconEye size={16} stroke={1.5} />}
                onClick={() => {
                  navigate(
                    `/patients/${patientId}/billing/${currentConsultationId}`
                  );
                }}>
                {t("view-bill")}
              </Menu.Item>
              <Menu.Item
                disabled={!item?.prescriptionId}
                leftSection={<IconEye size={16} stroke={1.5} />}
                onClick={() => {
                  navigate(
                    `/patients/${patientId}/prescription/${item.prescriptionId}/medication`
                  );
                }}>
                {t("view-prescription")}
              </Menu.Item>
              {can("consultations.delete") && (
                <>
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconFile size={16} stroke={1.5} />}
                    onClick={() => {
                      const id = showLoadingNotification({});
                      generateConsultationReport(item.id as string, {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "generate-report-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      });
                    }}>
                    {t("generate-report")}
                  </Menu.Item>
                  <PermissionGate permission="consultations.delete"><Menu.Item
                    leftSection={<IconArchive size={16} stroke={1.5} />}
                    onClick={() => {
                      const id = showLoadingNotification({});
                      archiveConsultation(
                        {
                          consultationId: item.id as string,
                          patientId: item.patientId as string,
                        },
                        {
                          onSuccess: () => {
                            showSuccessNotification({
                              id,
                              title: "success",
                              message: "archive-consultation-success",
                            });
                          },
                          onError: (res: any) => {
                            handleErrors(res, "mutation", id);
                          },
                        }
                      );
                    }}>
                    {t("archive-consultation")}
                  </Menu.Item></PermissionGate>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    ));
  }, [
    i18n.language,
    currentConsultationId,
    isToggleConsultationReleaseLoading,
    data,
  ]);

  useEffect(() => {
    if (isRefetching) {
      setIsRefreshing(true);
    } else setIsRefreshing(false);
  }, [isRefetching]);

  return (
    <>
      <Table
        ref={tableRef}
        withRowBorders={true}
        mt={"1rem"}
        miw={1280}
        withTableBorder
        highlightOnHover
        highlightOnHoverColor="var(--clr-gray-100)">
        <Table.Thead>
          <Table.Tr bg={"var(--clr-gray-100)"}>
            {/* <Table.Th></Table.Th> */}
            <Table.Th>{t("consultation")}</Table.Th>
            <Table.Th>{t("created-by")}</Table.Th>
            <Table.Th>{t("status")}</Table.Th>
            <Table.Th>{t("visibility")}</Table.Th>
            <Table.Th>{t("action")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {renderLoadingErrorData({
            isLoading: isLoading || fetchStatus === "fetching",
            isError: isError,
            isSuccess: isSuccess,
            dataLength: data?.newData?.length || 0,
            emptyDataComponent: (
              <TableEmptyRow colSpan={columnCount} message={t("empty-data")} />
            ),
            errorComponent: (
              <TableErrorRow
                colSpan={columnCount}
                message={t("encountered-error")}
              />
            ),
            loadingComponent: (
              <TableSkeletonRows
                columns={columnCount}
                skeletonProps={{
                  widths: ["md", "lg", "md", "md"],
                }}
              />
            ),
            dataComponent: consultationRows,
          })}
        </Table.Tbody>
      </Table>
      <TablePagination
        paginationData={data?.pagination || {}}
        paginatedRows={data?.newData?.length || 0}
        onPageChange={setCurrentPage}
        isLoading={isLoading || fetchStatus === "fetching"}
      />

      {/* {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={consultationDataColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null} */}
    </>
  );
};
